import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import HomePage from './HomePage';
import ItemsPage from './ItemsPage';
import MyAccountPage from './MyAccountPage';
import Modal from './Modal';
import LoginModal from './LoginModal';

// Mock API base URL - replace with your actual MockAPI endpoint
const API_BASE = 'https://688e5a2fa459d5566b14bcb2.mockapi.io/api/v1';

const App = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [username, setUsername] = useState('');
  const [items, setItems] = useState([
    { id: 1, name: 'Test Item 1', category: 'Category A', status: 'Active', isChecked: true },
    { id: 2, name: 'Test Item 2', category: 'Category B', status: 'Inactive', isChecked: false },
  ]);
  const [countries, setCountries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  // Load data from localStorage on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        // Try HTTP API first
        const response = await fetch(`${API_BASE}/travels`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });

        if (response.ok) {
          const result = await response.json();
          // MockAPI returns array directly, not wrapped in success object
          if (Array.isArray(result)) {
            setCountries(result);
            setIsDataLoaded(true);
            return;
          }
        }
      } catch (error) {
        // If API fails, fall back to localStorage
        console.log('GET API not available, using localStorage');
      }

      // Original fallback logic - load from localStorage
      const savedCountries = localStorage.getItem('travel-destinations');
      if (savedCountries) {
        try {
          const parsedCountries = JSON.parse(savedCountries);
          setCountries(parsedCountries);
        } catch (error) {
          console.error('Error loading saved destinations:', error);
        }
      }
      setIsDataLoaded(true);
    };

    loadData();

    // Load login state from localStorage
    const savedLoginState = localStorage.getItem('isLoggedIn');
    const savedUsername = localStorage.getItem('username');
    if (savedLoginState === 'true' && savedUsername) {
      setIsLoggedIn(true);
      setUsername(savedUsername);
    }
  }, []);

  // Save data to localStorage whenever countries changes (but only after initial load)
  useEffect(() => {
    if (isDataLoaded) {
      localStorage.setItem('travel-destinations', JSON.stringify(countries));
    }
  }, [countries, isDataLoaded]);

  // Save login state to localStorage
  useEffect(() => {
    localStorage.setItem('isLoggedIn', isLoggedIn.toString());
    if (username) {
      localStorage.setItem('username', username);
    } else {
      localStorage.removeItem('username');
    }
  }, [isLoggedIn, username]);

  const handleCreateItem = async (formData) => {
    if (formData.name && formData.category && formData.tripCost) {
      setIsLoading(true);

      try {
        // Try HTTP API first
        const response = await fetch(`${API_BASE}/travels`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            destination: formData.name,
            travelType: formData.category,
            status: formData.status,
            isFavorite: formData.isChecked,
            tripCost: parseInt(formData.tripCost)
          })
        });

        if (response.ok) {
          const result = await response.json();
          console.log('API Success:', result);
          setCountries([...countries, result]);
          setIsLoading(false);
          setIsModalOpen(false);
          return;
        } else {
          console.log('API Error:', response.status, response.statusText);
          throw new Error(`API Error: ${response.status}`);
        }
      } catch (error) {
        console.log('API Request Failed:', error.message);
        console.log('Falling back to localStorage method');
      }

      // Fallback logic with 3-second delay
      setTimeout(() => {
        const newItem = {
          id: Date.now(),
          destination: formData.name,
          travelType: formData.category,
          status: formData.status,
          isFavorite: formData.isChecked,
          tripCost: parseInt(formData.tripCost)
        };
        setCountries([...countries, newItem]);
        setIsLoading(false);
        setIsModalOpen(false);
      }, 3000);
    }
  };

  const handleDeleteItem = async (itemId) => {
    try {
      // Try HTTP API first
      const response = await fetch(`${API_BASE}/travels/${itemId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.ok) {
        // MockAPI DELETE returns the deleted item or just success
        setCountries(countries.filter(item => item.id !== itemId));
        return;
      }
    } catch (error) {
      // If API fails, fall back to original localStorage logic
      console.log('Delete API not available, using fallback deletion');
    }

    // Original fallback logic
    setCountries(countries.filter(item => item.id !== itemId));
  };

  const handleUpdateStatus = async (itemId, newStatus) => {
    try {
      // Try HTTP API first - using PUT for full resource update
      const currentItem = countries.find(item => item.id === itemId);
      const updatedItem = { ...currentItem, status: newStatus };

      const response = await fetch(`${API_BASE}/travels/${itemId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedItem)
      });

      if (response.ok) {
        const result = await response.json();
        // MockAPI returns the updated item directly
        setCountries(countries.map(item =>
          item.id === itemId ? result : item
        ));
        return;
      }
    } catch (error) {
      // If API fails, fall back to original localStorage logic
      console.log('Update API not available, using fallback update');
    }

    // Original fallback logic
    setCountries(countries.map(item =>
      item.id === itemId
        ? { ...item, status: newStatus }
        : item
    ));
  };

  const handleDownload = () => {
    const data = JSON.stringify(countries, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'travel-destinations.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileUpload = (uploadedData) => {
    // Add uploaded items to existing countries
    const newItems = uploadedData.map(item => ({
      ...item,
      id: Date.now() + Math.random() // Ensure unique IDs
    }));
    setCountries([...countries, ...newItems]);
  };

  const handleLogin = async (loginUsername, loginPassword) => {
    try {
      // Try HTTP login first
      const response = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: loginUsername,
          password: loginPassword
        })
      });

      const result = await response.json();
      if (result.success || response.ok) {
        setIsLoggedIn(true);
        setUsername(result.username || loginUsername);
        return;
      }
    } catch (error) {
      // If API fails, fall back to original fake auth (any credentials work)
      console.log('Login API not available, using fallback auth');
    }

    // Original fallback logic - accept any credentials
    setIsLoggedIn(true);
    setUsername(loginUsername);
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setUsername('');
  };

  const handleLoginClick = () => {
    setIsLoginModalOpen(true);
  };

  // Protected Route Component
  const ProtectedRoute = ({ children }) => {
    return isLoggedIn ? children : <Navigate to="/" replace />;
  };

  return (
    <Router>
      <div className="app" id="app">
        {/* GLOBAL LOADER */}
        {isLoading && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 99999
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '40px',
              textAlign: 'center',
              boxShadow: '0 10px 50px rgba(0, 0, 0, 0.5)'
            }}>
              <div style={{
                width: '50px',
                height: '50px',
                border: '5px solid #f3f3f3',
                borderTop: '5px solid #3498db',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 20px'
              }}></div>
              <p style={{ fontSize: '18px', fontWeight: 'bold', color: '#333', margin: 0 }}>
                Saving travel item...
              </p>
            </div>
          </div>
        )}

        <Routes>
          <Route path="/" element={
            <HomePage
              isLoggedIn={isLoggedIn}
              username={username}
              onLoginClick={handleLoginClick}
            />
          } />
          <Route path="/items" element={
            <ItemsPage
              items={items}
              countries={countries}
              setIsModalOpen={setIsModalOpen}
              handleDownload={handleDownload}
              onDeleteItem={handleDeleteItem}
              onFileUpload={handleFileUpload}
              onUpdateStatus={handleUpdateStatus}
              isLoggedIn={isLoggedIn}
              username={username}
              onLoginClick={handleLoginClick}
            />
          } />
          <Route path="/my-account" element={
            <ProtectedRoute>
              <MyAccountPage
                username={username}
                onLogout={handleLogout}
              />
            </ProtectedRoute>
          } />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>

        {/* Modals */}
        {isModalOpen && (
          <Modal
            setIsModalOpen={setIsModalOpen}
            onCreateItem={handleCreateItem}
            isLoading={isLoading}
          />
        )}

        <LoginModal
          isOpen={isLoginModalOpen}
          setIsOpen={setIsLoginModalOpen}
          onLogin={handleLogin}
        />

        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </Router>
  );
};

export default App;